import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import { useNetworkIdRedirect } from "./common/hooks";
import { Container, Row, Col, Tab, Button, Form, Nav, Breadcrumb } from "react-bootstrap";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import TabTableWithSearch from './TabTableWithSearch';
import refreshIcon from "assets/svg/refresh-green.svg";
import { IconButton } from "@mui/material";
import { TimeRangeSelection } from "components/common/TimeRangeDropdown";
import moment from "moment";
import { CHART_CUSTOM_RANGE_OPTION } from "constant";
import { 
    getGatewayAlertNotification,
    getOneRegistry,
    getRegistryConfig,
    getGwAndLDSUStatus,
    getAllLastBusReading,
    getSensorLastReading
} from "service/gatewayService";
import { HoverAuthorizeTooltip } from "components/authorize/AuthorizeTooltip";
import { WalletAlertComponent, walletModalTrigger } from "hooks/wallet";
import { useCheckIfBasicPlan } from "hooks/useCheckIfBasicPlan";
import { LoadingModal } from "components/modals/ModalTemplate";
import { isHttpSuccess, convertSecondsToPrometheusTime } from "utils/functions";
import { get } from "lodash";
import { useOrganisation } from "hooks/organization";
import { getLDSBusInfo } from "utils/gatewayFunctions";
import { getCalendarFormat } from "utils/datetime";
import {
    queryTagStatusFromUIDSAIDsWithTimestamp,
} from "service/networkService";
import {
    countDevicesByType,
    countDevicesByTagUIDSAIDs,
    getTagDeviceUID,
    isRoomOrZoneByName,
    isGroupOrHubByName,
    addDeviceToUIDSAIDsMap,
    isSensorFromEntityDevice,
    getSensorsFromConfigForZoneTag,
    getIOControllerActuatorsFromConfig,
    shouldExcludeDeviceFromEntityList,
    // Use same exclusion logic as NetworkDetails zone counts
    // to keep ZoneDetails sensor list consistent.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    // (imported for clarity even if tree‑shaken)
    // shouldExcludeDeviceFromCount,
    selectEntityUUID,
} from "./common/deviceUtils";
import {
    updateAlertStatus as updateAlertStatusCommon,
    returnCheckboxStatus as returnCheckboxStatusCommon,
    returnDisabledStatus as returnDisabledStatusCommon
} from "./common/alertUtils";
import { buildPromQLQueryFromUIDSAIDsMap, selectUIDSAIDsForUID } from "./common/promqlUtils";
import {
    createDefaultLast24HoursTimeRange,
    getPrometheusTimeRangeForSensorReadings,
    pickBestSensorBusReadingForSAID,
    mergeSensorLastReadingWithMinMaxFallback,
} from "./common/sensorStatsUtils";
import {
    formatSensorReadingCellValue,
    formatSensorMinMaxCellValue,
    getSensorUnit,
} from "./common/sensorValueFormatter";
import { fetchTags as fetchTagsCommon } from "./common/tagService";
import { createSensorNameRenderer } from "./common/sensorRenderers";
import { renderSensorsControllersFilters } from "./common/filters";
import { getControllerStatusInfo } from "./common/controllerStatus";
import { getControllerStatusFromReading2 } from "./common/controllerReadingUtils";
import { getRowToForContext, type NetworkRowToContext } from "./common/getRowTo";
import { renderStatusCellCommon, renderLastActiveCellCommon } from "./common/statusRenderer";
import { getNetworkColumnLabel } from "./common/columnLabelUtils";

const tabKeys = [
    "rooms",
    "sensors",
    "controllers",
];

const tabLabels: { [key: string]: string } = {
    rooms: "Rooms in this Zone",
    sensors: "Sensors in this Zone",
    controllers: "Actuators in this Zone",
};

/** Alphabetical room status sort; missing values sort last (useCollectSort no-data). */
const roomStatusSortValue = (status: unknown): string | number => {
    if (status == null || status === "") return Number.POSITIVE_INFINITY;
    if (typeof status !== "string" && typeof status !== "number" && typeof status !== "boolean") {
        return Number.POSITIVE_INFINITY;
    }
    return `${status}`.trim().toLowerCase();
};

const ROOM_STATUS_QUERY_TIMEOUT_MS = 10000;

const queryRoomStatusWithTimeout = async (
    gatewayId: string,
    roomUIDSAIDsForStatus: Map<string, Set<number>>
): Promise<{ status: string; timestamp: number | null }> =>
    Promise.race([
        queryTagStatusFromUIDSAIDsWithTimestamp(
            gatewayId,
            (queryGatewayId: string) =>
                buildPromQLQueryFromUIDSAIDsMap(queryGatewayId, roomUIDSAIDsForStatus)
        ),
        new Promise<{ status: string; timestamp: number | null }>((resolve) => {
            setTimeout(
                () => resolve({ status: "Unknown", timestamp: null }),
                ROOM_STATUS_QUERY_TIMEOUT_MS
            );
        }),
    ]);

// Extracted component renderers
interface NameLinkRendererProps {
    row: any;
    col: string;
    tabKey: string;
    onRowClick: (row: any, tabKey: string) => void;
    getRowTo?: (row: any, tabKey: string) => string;
}

const NameLinkRenderer: React.FC<NameLinkRendererProps> = ({ row, col, tabKey, onRowClick, getRowTo }) => {
    const to = getRowTo?.(row, tabKey) ?? "#";
    return (
        <Link
            to={to}
            onClick={to === "#" ? (e) => {
                e.preventDefault();
                onRowClick(row, tabKey);
            } : undefined}
        >
            {row[col]}
        </Link>
    );
};

interface ReadingRendererProps {
    row: any;
    col: string;
}

const ReadingRenderer: React.FC<ReadingRendererProps> = ({ row, col }) => {
    // Align with NetworkDetails: format from raw readings value, so display matches sorting.
    return (
        <span style={{ color: "#00d1b2" }}>
            {formatSensorReadingCellValue(row, row[col])}
        </span>
    );
};

interface MinMaxRendererProps {
    row: any;
    col: string;
}

const MinMaxRenderer: React.FC<MinMaxRendererProps> = ({ row, col }) => (
    <span>{row[col] ?? "-"}</span>
);

interface AlertRendererProps {
    row: any;
    createAlertToggleHandler: (ldsuUuid: string, said: number) => () => void;
    returnCheckboxStatus: (ldsuUuid: string, said: number) => boolean;
    returnDisabledStatus: (ldsuUuid: string, said: number) => boolean;
}

const AlertRenderer: React.FC<AlertRendererProps> = ({ 
    row, 
    createAlertToggleHandler, 
    returnCheckboxStatus, 
    returnDisabledStatus 
}) => (
    <HoverAuthorizeTooltip permission="gateway:update">
        <div>
            <Form.Check
                type="switch"
                id={`switch-${row.ldsu_uuid}-${row.SAID}`}
                label=""
                checked={returnCheckboxStatus(
                    row.ldsu_uuid,
                    row.SAID
                )}
                disabled={returnDisabledStatus(
                    row.ldsu_uuid,
                    row.SAID
                )}
                onChange={createAlertToggleHandler(
                    row.ldsu_uuid,
                    row.SAID
                )}
            />
        </div>
    </HoverAuthorizeTooltip>
);

// Factory functions to create render functions outside the component
const createReadingRenderFunction = () => (value: any, row: any) => (
    <ReadingRenderer row={row} col="reading" />
);

const createMinMaxRenderFunction = () => (value: any, row: any) => (
    <MinMaxRenderer row={row} col="minMax" />
);

const createLastActiveRenderFunction = (
    onRefreshSensor: (
        e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
        sensor: any
    ) => void,
    isRefreshing: boolean
) => (value: any, row: any) =>
    renderLastActiveCellCommon({
        row,
        col: "lastactive",
        tabKey: "sensors",
        onRefreshSensor,
        refreshAltText: "refresh sensor 2",
    });

const createAlertRenderFunction = (
    createAlertToggleHandler: (ldsuUuid: string, said: number) => () => void,
    returnCheckboxStatus: (ldsuUuid: string, said: number) => boolean,
    returnDisabledStatus: (ldsuUuid: string, said: number) => boolean
) => (value: any, row: any) => (
    <AlertRenderer
        row={row}
        createAlertToggleHandler={createAlertToggleHandler}
        returnCheckboxStatus={returnCheckboxStatus}
        returnDisabledStatus={returnDisabledStatus}
    />
);

const ZoneDetails = (): JSX.Element => {
    const { id, zoneId } = useParams<{ id: string; zoneId: string }>();
    const [activeTab, setActiveTab] = useState("rooms");
    const [network, setNetwork] = useState<any>(null);
    const [zone, setZone] = useState<any>(null);
    const [zoneName, setZoneName] = useState("");
    const [isZoneNameEditing, setIsZoneNameEditing] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [rooms, setRooms] = useState<any[]>([]);
    const [sensors, setSensors] = useState<any[]>([]);
    const [controllers, setControllers] = useState<any[]>([]);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isRoomStatusRefreshing, setIsRoomStatusRefreshing] = useState(false);
    const history = useHistory();
    const { currentOrgId } = useOrganisation();
    const { validateAndRedirect } = useNetworkIdRedirect(id);
    
    // Sensor-specific state
    const [selectedSensorTypes, setSelectedSensorTypes] = useState<string[]>([]);
    const [selectedActuatorTypes, setSelectedActuatorTypes] = useState<string[]>([]);
    const timeRange = useMemo<TimeRangeSelection>(
        () => createDefaultLast24HoursTimeRange(),
        []
    );

    // Alert state management
    const [alertUUIDList, setAlertUUIDList] = useState<any[]>([]);
    const [isAlertLoading, setIsAlertLoading] = useState(false);
    const { IS_BASIC_PLAN } = useCheckIfBasicPlan();
    const zoneTagUIDSAIDsRef = useRef<Map<string, Set<number>>>(new Map());
    const roomTagUIDSAIDsByNameRef = useRef<Map<string, Map<string, Set<number>>>>(new Map());

    // Helper to render a timestamp in a consistent calendar format
    const renderDateTime = useCallback((timestamp?: number | string): string => {
        if (!timestamp) {
            return "";
        }
        return moment(timestamp, "X").calendar(getCalendarFormat());
    }, []);

    // Process sensors for display
    const processedSensors = useMemo(() => {
        return sensors.map((sensor: any) => {
            // Extract minmax from sensor to avoid duplicate column
            const { minmax, ...sensorWithoutMinmax } = sensor;
            const resolvedUnit = getSensorUnit(sensor);

            const rawTimestamp = sensor.readings?.timestamp;
            const lastActive = renderDateTime(rawTimestamp);

            const ldsuUUID = sensor.ldsu_uuid ?? sensor.UID ?? "";
            const saidValue = sensor.SAID ?? 0;

            return {
                // Display fields
                sensor_name: sensor.sensor_name ?? sensor.name ?? "-",
                sensor_type: sensor.sensor_type ?? sensor.type ?? "-",
                type: sensor.sensor_type ?? sensor.type ?? "-",
                // Raw value; actual formatting handled by ReadingRenderer to match sorting
                reading: sensor.readings?.value ?? "-",
                readingSortValue:
                    sensor.readings?.value != null &&
                    sensor.readings?.value !== "-" &&
                    !Number.isNaN(Number(sensor.readings.value))
                        ? Number(sensor.readings.value)
                        : Number.POSITIVE_INFINITY,
                minMax: sensor.readings?.minmax
                    ? formatSensorMinMaxCellValue({
                        readings: sensor.readings,
                        accuracy: sensor.accuracy,
                        unit: resolvedUnit,
                    })
                    : "-",
                minMaxSortValue: (() => {
                    const arr = sensor.readings?.minmax;
                    if (!arr || !Array.isArray(arr) || arr.length < 1) return Number.POSITIVE_INFINITY;
                    const v = arr[0];
                    if (v == null || v === "-" || Number.isNaN(Number(v))) return Number.POSITIVE_INFINITY;
                    return Number(v);
                })(),
                lastactive: lastActive,
                // Original sensor fields (preserved for navigation)
                ldsu_uuid: ldsuUUID,
                SAID: saidValue,
                ldsu_name: sensor.ldsu_name ?? sensor.name ?? "-",
                alert: "",
                // For alert column sorting: 1 if enabled, 0 if disabled
                alertSortValue: returnCheckboxStatusCommon(
                    alertUUIDList as any,
                    ldsuUUID,
                    saidValue
                )
                    ? 1
                    : 0,
                accuracy: sensor.accuracy,
                unit: resolvedUnit,
                readings: sensor.readings,
                // Preserve all original sensor fields except minmax (to avoid duplicate column)
                ...sensorWithoutMinmax
            };
        });
    }, [sensors, alertUUIDList, renderDateTime]);

    // Filter sensors by selected types
    const filteredSensorList = useMemo(() => {
        if (!processedSensors.length || selectedSensorTypes.length === 0) {
            return processedSensors;
        }
        return processedSensors.filter(sensor => 
            selectedSensorTypes.includes(sensor.type)
        );
    }, [processedSensors, selectedSensorTypes]);
    
    // Sensor types for filtering
    const sensorTypes = useMemo(() => {
        if (activeTab !== 'sensors' || !processedSensors || processedSensors.length === 0) return [];
        const types: { [id: string]: string } = {};
        for (const row of processedSensors) {
            const type = row.type;
            if (type && !types[type]) {
                types[type] = type;
            }
        }
        return Object.keys(types).map((id) => ({ id, name: id }));
    }, [processedSensors, activeTab]);

    // Derive actuator types for filtering
    const actuatorTypes = useMemo(() => {
        if (!controllers.length) return [];
        const types: { [id: string]: string } = {};
        for (const row of controllers) {
            const type = row.type;
            if (type && !types[type]) {
                types[type] = type;
            }
        }
        return Object.keys(types).map((id) => ({ id, name: id }));
    }, [controllers]);

    // Filter controllers by selected actuator types
    const filteredControllers = useMemo(() => {
        if (!controllers.length || selectedActuatorTypes.length === 0) {
            return controllers;
        }
        return controllers.filter((actuator: any) =>
            selectedActuatorTypes.includes(actuator.type)
        );
    }, [controllers, selectedActuatorTypes]);

    // Process controllers for display (status from last bus reading only)
    const processedControllers = useMemo(() => {
        return filteredControllers.map((actuator: any) => {
            const { status } = getControllerStatusInfo(actuator);
            const lastActiveTs = actuator.readings?.timestamp ?? actuator.timestamp;
            return {
                name: actuator.name || "-",
                type: actuator.type || "-",
                status,
                lastActive: renderDateTime(lastActiveTs),
                UID: actuator.UID,
                said: actuator.said,
            };
        });
    }, [filteredControllers, renderDateTime]);

    // Tab detection logic (needed by processTabData)
    const getTabType = (tabKey: string, data: any[]) => {
        if (!data || data.length === 0) return false;
        
        const firstRow = data[0];
        
        switch (tabKey) {
            case 'sensors':
                return typeof firstRow.sensor_name === 'string' &&
                       typeof firstRow.sensor_type === 'string' &&
                       typeof firstRow.readings === 'object';
            case 'controllers':
                return typeof firstRow.name === 'string' &&
                       typeof firstRow.type === 'string' &&
                       typeof firstRow.status === 'string' &&
                       !('reading' in firstRow);
            case 'rooms':
                return typeof firstRow.name === 'string' &&
                       typeof firstRow.sensors === 'number' &&
                       typeof firstRow.controllers === 'number' &&
                       typeof firstRow.status === 'string' &&
                       !('rooms' in firstRow);
            default:
                return false;
        }
    };

    // Preprocess data for controllers tab - merge status and lastActive
    const processTabData = (key: string, data: any[]) => {
        if (getTabType('controllers', data)) {
            return data.map((row: any) => ({
                ...row,
                status: `${row.status} ${row.lastActive}`,
                // Remove lastActive since it's now part of status
            }));
        }
        return data;
    };

    const tabData: { [key: string]: any[] } = {
        rooms: rooms.map((room: any) => ({
            ...room,
            statusSortValue: roomStatusSortValue(room.status),
        })),
        sensors: filteredSensorList.length > 0 ? filteredSensorList : processedSensors,
        controllers: processTabData('controllers', processedControllers),
    };

    // Alert management functions
    const fetchAlertData = useCallback(async () => {
        const ldsuGatewayId = id; // Use panlNetworkId from route params
        
        try {
            const alertResponse = await getGatewayAlertNotification(ldsuGatewayId);
            if (isHttpSuccess(alertResponse.status)) {
                const alerts = alertResponse.data || [];
                // Transform alert data to match sensor structure (device_id -> source, enabled -> status)
                const transformedAlerts = alerts.map((alert: any) => ({
                    uuid: alert.uuid,
                    source: alert.device_id,
                    said: alert.said,
                    gatewayUUID: alert.gateway_id,
                    status: alert.enabled,
                    notice: alert.notice,
                }));
                setAlertUUIDList(transformedAlerts);
            }
        } catch (error) {
            console.error("Error fetching alert data:", error);
        }
    }, [id]);

    const updateAlertStatus = useCallback(async (source: string, said: string) => {
        await updateAlertStatusCommon(
            alertUUIDList as any,
            source,
            said,
            setAlertUUIDList as any,
            setIsAlertLoading
        );
    }, [alertUUIDList]);

    const returnCheckboxStatus = useCallback((source: string, said: number) => {
        return returnCheckboxStatusCommon(alertUUIDList as any, source, said);
    }, [alertUUIDList]);

    const returnDisabledStatus = useCallback((source: string, said: number) => {
        return returnDisabledStatusCommon(alertUUIDList as any, source, said);
    }, [alertUUIDList]);

    /**
     * Fetches tags from API
     * @param organizationId - Organization ID
     * @param tagValue - Optional tag value filter (e.g., zone name)
     * @returns Array of tag objects
     */
    const fetchTags = useCallback(
        async (organizationId: string, tagValue?: string) =>
            fetchTagsCommon(organizationId, { value: tagValue }),
        []
    );

    /**
     * Extracts device UIDs from tag API devices array
     * Tag API format: { devices: [["gatewayId", "UID", SAID], ...] }
     * @param tags - Array of tag objects from API
     * @returns Set of device UIDs
     */
    const extractTagUIDs = useCallback((tags: any[]): Set<string> => {
        const uids = new Set<string>();
        for (const tag of tags) {
            if (tag.devices && Array.isArray(tag.devices)) {
                for (const device of tag.devices) {
                    const uid = getTagDeviceUID(device);
                    if (uid) {
                        uids.add(uid);
                    }
                }
            }
        }
        return uids;
    }, []);

    /**
     * Extracts UID-SAID pairs from tag API devices array
     * Tag API format: { devices: [["gatewayId", "UID", SAID], ...] }
     * @param tags - Array of tag objects from API
     * @returns Map of UID -> Set of SAIDs
     */
    const extractTagUIDSAIDs = useCallback((tags: any[]): Map<string, Set<number>> => {
        const uidSaidMap = new Map<string, Set<number>>();
        for (const tag of tags) {
            if (tag.devices && Array.isArray(tag.devices)) {
                for (const device of tag.devices) {
                    addDeviceToUIDSAIDsMap(device, uidSaidMap);
                }
            }
        }
        return uidSaidMap;
    }, []);

    /**
     * Extracts device UIDs from registry API response
     * @param registryData - Registry API response data
     * @returns Set of device UIDs
     */
    const extractRegistryUIDs = useCallback((registryData: any): Set<string> => {
        const uids = new Set<string>();
        if (registryData?.info?.devices && Array.isArray(registryData.info.devices)) {
            for (const device of registryData.info.devices) {
                if (device?.UID) {
                    uids.add(String(device.UID));
                } else if (Array.isArray(device) && device.length > 0) {
                    uids.add(String(device[0]));
                } else if (typeof device === 'string') {
                    uids.add(device);
                }
            }
        }
        return uids;
    }, []);

    // Helper function to find zone UIDs (intersection of tag UIDs and registry UIDs)
    const findZoneUIDs = useCallback((zoneTagUIDs: Set<string>, registryUIDs: Set<string>): Set<string> => {
        const zoneUIDs = new Set<string>();
        for (const uid of Array.from(zoneTagUIDs)) {
            if (registryUIDs.has(uid)) {
                zoneUIDs.add(uid);
            }
        }
        return zoneUIDs;
    }, []);

    // Helper function to find zone room device UIDs
    // Uses NAME/NICK field first (more reliable when same UID appears in tags API), then falls back to OBJ
    const findZoneRoomDeviceUIDs = useCallback((zoneUIDs: Set<string>, devices: any[]): Set<string> => {
        const zoneRoomDeviceUIDs = new Set<string>();
        for (const uid of Array.from(zoneUIDs)) {
            const registryDevice = devices.find((device: any) => {
                const deviceUID = device?.UID || device?.uid || "";
                return String(deviceUID) === uid;
            });
            
            if (registryDevice) {
                // Check NAME field first (more reliable when UIDs overlap in tags API)
                const name = registryDevice?.NAME;
                const nick = registryDevice?.NICK;
                if (isRoomOrZoneByName(name) || isRoomOrZoneByName(nick)) {
                    zoneRoomDeviceUIDs.add(uid);
                    continue;
                }
                
                // Fallback to OBJ value
                const obj = registryDevice?.OBJ;
                if (obj === "45058" || obj === 45058) {
                    zoneRoomDeviceUIDs.add(uid);
                }
            }
        }
        return zoneRoomDeviceUIDs;
    }, []);

    // Helper function to check if room tag contains zone room device UIDs
    const roomTagContainsZoneDevices = useCallback((tag: any, zoneRoomDeviceUIDs: Set<string>): boolean => {
        if (!tag.value?.startsWith("room/")) {
            return false;
        }
        const roomTagUIDs = extractTagUIDs([tag]);
        for (const roomUID of Array.from(roomTagUIDs)) {
            if (zoneRoomDeviceUIDs.has(roomUID)) {
                return true;
            }
        }
        return false;
    }, [extractTagUIDs]);

    // Helper function to collect sensors and actuators from all buses
    const collectBusDevices = useCallback((devices: any[], configList: any[], statusDevices: any[]): { sensors: any[]; actuators: any[] } => {
        const allSensors: any[] = [];
        const allActuators: any[] = [];

        for (let busNumber = 1; busNumber <= 3; busNumber++) {
            const { sensors: busSensors, actuators: busActuators } = getLDSBusInfo(
                busNumber.toString(),
                devices,
                configList,
                statusDevices
            );

            // Filter out only sensors that belong to zone/room/group/hub entity devices (e.g. "Binary Digital Input").
            // Include VIRTUAL/auxiliary hub sensors so they show when the zone has such devices.
            const actualSensors = busSensors.filter((s: any) => !isSensorFromEntityDevice(s, devices));

            // Merge base actuators from LDSU with IO Controller actuators built from config
            const ioControllerActuators = getIOControllerActuatorsFromConfig(
                busNumber.toString(),
                devices,
                configList,
                statusDevices
            );

            const actuatorMap = new Map<string, any>();
            const addActuator = (actuator: any) => {
                const key = `${actuator.UID}:${actuator.said ?? actuator.SAID ?? 0}`;
                if (!actuatorMap.has(key)) {
                    actuatorMap.set(key, actuator);
                }
            };

            (busActuators || []).forEach(addActuator);
            (ioControllerActuators || []).forEach(addActuator);

            const mergedActuators = Array.from(actuatorMap.values());

            // At zone level, trust per‑channel CLS/type from getLDSBusInfo /
            // getActuatorList and do not re‑filter by device OBJ. This avoids
            // hiding valid actuator channels on auxiliary hubs.
            const filteredActuators = mergedActuators;

            allSensors.push(...actualSensors);
            allActuators.push(...filteredActuators);
        }

        return { sensors: allSensors, actuators: allActuators };
    }, []);

    // (Deprecated) Helper to fetch and process sensor readings without time range
    // Kept for reference but no longer used now that timeRange-aware mapSensorsWithReadings is in place.

    // Helper function to build UID to device map (includes OBJ, NAME, NICK for identification)
    const buildUIDToDeviceMap = useCallback((devices: any[]): Map<string, any> => {
        const uidToDeviceMap = new Map<string, any>();
        for (const device of devices) {
            const deviceUID = device?.UID || device?.uid || "";
            if (deviceUID) {
                uidToDeviceMap.set(String(deviceUID), device);
            }
        }
        return uidToDeviceMap;
    }, []);

    // Map readings to sensors (helper function for refresh and initial load)
    const mapSensorsWithReadings = useCallback(
        async (sensorsList: any[], timeRange?: string, offset?: string) => {
            if (!id || !sensorsList.length) return sensorsList;

            try {
                const readingPromises = [];
                for (let busNumber = 1; busNumber <= 3; busNumber++) {
                    readingPromises.push(
                        getAllLastBusReading(
                            id,
                            busNumber.toString(),
                            timeRange,
                            offset
                        )
                    );
                }
                const readingResults = await Promise.all(readingPromises);
                const allReadings: any[] = [];
                for (const result of readingResults) {
                    if (isHttpSuccess(result.status)) {
                        allReadings.push(...(result.data?.devices || []));
                    }
                }

                return sensorsList.map((sensor: any) => {
                    const bestReading = pickBestSensorBusReadingForSAID(
                        sensor,
                        allReadings
                    );

                    if (bestReading) {
                        sensor.readings = {
                            value: bestReading.value,
                            highest: bestReading.highest,
                            lowest: bestReading.lowest,
                            timestamp: bestReading.timestamp,
                            minmax: [bestReading.highest, bestReading.lowest],
                        };
                    }

                    return sensor;
                });
            } catch (error) {
                console.error("ZoneDetails: Error mapping sensor readings:", error);
                return sensorsList;
            }
        },
        [id]
    );

    // Helper function to filter zone sensors
    const filterZoneSensors = useCallback((
        sensorsWithReadings: any[],
        zoneUIDs: Set<string>,
        zoneTagUIDSAIDs: Map<string, Set<number>>,
        uidToDeviceMap: Map<string, any>
    ): any[] => {
        return sensorsWithReadings.filter((sensor: any) => {
            const sensorUID = sensor.ldsu_uuid || sensor.UID || "";
            const sensorSAID = sensor.SAID !== undefined && sensor.SAID !== null ? Number(sensor.SAID) : 0;
            
            if (!zoneUIDs.has(sensorUID)) {
                return false;
            }
            
            const allowedSAIDs = zoneTagUIDSAIDs.get(sensorUID);
            if (allowedSAIDs && !allowedSAIDs.has(sensorSAID)) {
                return false;
            }
            
            const device = uidToDeviceMap.get(sensorUID);
            if (!device) {
                return false;
            }

            // Check NAME/NICK field first (more reliable when same UID appears in tags API)
            const name = device?.NAME;
            const nick = device?.NICK;
            if (isGroupOrHubByName(name) || isGroupOrHubByName(nick)) {
                // Exclude groups/hubs
                return false;
            }
            if (isRoomOrZoneByName(name) || isRoomOrZoneByName(nick)) {
                // Exclude rooms/zones
                return false;
            }

            // Include sensors from auxiliary hub devices (e.g. LDSBus 4in1) when they are in the zone tag
            return true;
        });
    }, []);

    // Helper function to filter zone actuators
    const filterZoneActuators = useCallback((
        allActuators: any[],
        zoneUIDs: Set<string>,
        zoneTagUIDSAIDs: Map<string, Set<number>>,
        uidToDeviceMap: Map<string, any>
    ): any[] => {
        return allActuators.filter((actuator: any) => {
            const actuatorUID = actuator.UID || "";
            const actuatorSAID = actuator.said !== undefined && actuator.said !== null ? Number(actuator.said) : 0;
            
            if (!zoneUIDs.has(actuatorUID)) {
                return false;
            }
            
            const allowedSAIDs = zoneTagUIDSAIDs.get(actuatorUID);
            if (allowedSAIDs && allowedSAIDs.size > 0 && !allowedSAIDs.has(actuatorSAID)) {
                return false;
            }
            
            const device = uidToDeviceMap.get(actuatorUID);
            if (!device) {
                return false;
            }

            // Check NAME/NICK field first (more reliable when same UID appears in tags API)
            const name = device?.NAME;
            const nick = device?.NICK;
            if (isGroupOrHubByName(name) || isGroupOrHubByName(nick)) {
                // Exclude groups/hubs
                return false;
            }
            if (isRoomOrZoneByName(name) || isRoomOrZoneByName(nick)) {
                // Exclude rooms/zones
                return false;
            }

            // Fallback to OBJ value check
            // Include actuators from auxiliary hub devices (e.g. IO Controller outputs)
            // when they are explicitly tagged in the zone.
            return true;
        });
    }, []);

    // Helper function to build rooms data
    // NOTE: Use per‑endpoint (UID+SAID) counting so the room table
    // matches the RoomDetails page semantics (room‑centric, per‑channel CLS/type).
    const buildRoomsData = useCallback((
        roomTags: any[],
        devices: any[],
        sensorsWithReadings: any[],
        allActuators: any[],
        uidToDeviceMap: Map<string, any>
    ): any[] => {
        const roomsData: any[] = [];
        for (const tag of roomTags) {
            const roomName = tag.value?.replace("room/", "") || "";
            // Build UID→Set<SAID> map from this room tag's devices
            const roomTagUIDSAIDs = extractTagUIDSAIDs([tag]);
            const roomUIDs = new Set<string>(Array.from(roomTagUIDSAIDs.keys()));

            // Count sensors for this room using per‑channel CLS/type and
            // excluding only entity devices via shouldExcludeDeviceFromEntityList,
            // same as RoomDetails.
            const roomSensors = sensorsWithReadings.filter((sensor: any) => {
                const sensorUID = sensor.ldsu_uuid || sensor.UID || "";
                const sensorSAID =
                    sensor.SAID !== undefined && sensor.SAID !== null
                        ? Number(sensor.SAID)
                        : 0;

                if (!roomUIDs.has(sensorUID)) {
                    return false;
                }

                const allowedSAIDs = roomTagUIDSAIDs.get(sensorUID);
                if (allowedSAIDs && !allowedSAIDs.has(sensorSAID)) {
                    return false;
                }

                const device = uidToDeviceMap.get(sensorUID);
                if (!device) {
                    return false;
                }

                return !shouldExcludeDeviceFromEntityList(device);
            });

            // Count actuators for this room using the same per‑channel semantics
            const roomActuators = allActuators.filter((actuator: any) => {
                const actuatorUID = actuator.UID || "";
                const actuatorSAID =
                    actuator.said !== undefined && actuator.said !== null
                        ? Number(actuator.said)
                        : 0;

                if (!roomUIDs.has(actuatorUID)) {
                    return false;
                }

                const allowedSAIDs = roomTagUIDSAIDs.get(actuatorUID);
                if (
                    allowedSAIDs &&
                    allowedSAIDs.size > 0 &&
                    !allowedSAIDs.has(actuatorSAID)
                ) {
                    return false;
                }

                const device = uidToDeviceMap.get(actuatorUID);
                if (!device) {
                    return false;
                }

                return !shouldExcludeDeviceFromEntityList(device);
            });

            const roomUuid = selectEntityUUID(roomUIDs, devices, roomName, "room");

            roomsData.push({
                name: roomName,
                uuid: roomUuid ?? undefined,
                sensors: roomSensors.length,
                controllers: roomActuators.length,
                status: "Unknown",
            });
        }
        return roomsData;
    }, [extractTagUIDSAIDs]);

    // Helper function to query zone status (with timestamp) using the same UID/SAID logic as NetworkDetails
    const getZoneStatus = useCallback(
        async (
            id: string,
            zoneTagUIDSAIDs: Map<string, Set<number>>,
            currentOrgId: string,
            zoneUuid?: string | null
        ): Promise<{ status: string; timestamp: number | null }> => {
            if (!currentOrgId || !id || zoneTagUIDSAIDs.size === 0) {
                return { status: "Unknown", timestamp: null };
            }
            try {
                const zoneUIDSAIDsForStatus = selectUIDSAIDsForUID(zoneTagUIDSAIDs, zoneUuid);
                const result = await queryTagStatusFromUIDSAIDsWithTimestamp(
                    id,
                    (gatewayId: string) =>
                        buildPromQLQueryFromUIDSAIDsMap(gatewayId, zoneUIDSAIDsForStatus)
                );
                return {
                    status: result.status,
                    timestamp: result.timestamp ?? null,
                };
            } catch (error) {
                console.error("Error querying zone status by UID:", error);
                return { status: "Unknown", timestamp: null };
            }
        },
        []
    );

    // Helper function to check if a device is a room (not a zone)
    const isDeviceRoom = useCallback((device: any): boolean => {
        if (!device) {
            return false;
        }
        const name = device?.NAME;
        const nick = device?.NICK;
        const nameLower = name ? String(name).toLowerCase().trim() : '';
        const nickLower = nick ? String(nick).toLowerCase().trim() : '';
        const isRoom = nameLower === 'room' || nickLower === 'room';
        const isZone = nameLower === 'zone' || nickLower === 'zone';
        
        // Skip if it's a zone device (zone should not appear as a room)
        if (isZone && !isRoom) {
            return false;
        }
        
        // Only proceed if it's actually a room (NAME="room" or OBJ=45058 without NAME="zone")
        const hasRoomName = isRoom;
        const hasRoomObj = !isZone && (device?.OBJ === "45058" || device?.OBJ === 45058);
        
        return hasRoomName || hasRoomObj;
    }, []);

    // Helper function to check if a room UID is already in a room tag
    const isRoomUIDInTag = useCallback((roomUID: string, roomTags: any[]): boolean => {
        return roomTags.some((tag: any) => {
            const roomTagUIDs = extractTagUIDs([tag]);
            return roomTagUIDs.has(roomUID);
        });
    }, [extractTagUIDs]);

    // Helper function to build direct zone rooms from zone room device UIDs
    const buildDirectZoneRooms = useCallback((
        zoneRoomDeviceUIDs: Set<string>,
        devices: any[],
        roomTags: any[]
    ): any[] => {
        const directZoneRooms: any[] = [];
        
        for (const roomUID of Array.from(zoneRoomDeviceUIDs)) {
            const registryDevice = devices.find((device: any) => {
                const deviceUID = device?.UID || device?.uid || "";
                return String(deviceUID) === roomUID;
            });
            
            if (!registryDevice || !isDeviceRoom(registryDevice)) {
                continue;
            }
            
            // Check if this room device is in a room tag (if so, it's already handled above)
            if (isRoomUIDInTag(roomUID, roomTags)) {
                continue;
            }
            
            // Only add if it's not already in a room tag
            const roomName = registryDevice?.NICK || registryDevice?.NAME || roomUID;
            const roomTagUIDs = new Set([roomUID]);
            const counts = countDevicesByType(roomTagUIDs, devices);
            directZoneRooms.push({
                name: roomName,
                sensors: counts.sensors,
                controllers: counts.controllers,
                status: "Unknown"
            });
        }
        
        return directZoneRooms;
    }, [isDeviceRoom, isRoomUIDInTag]);

    // Helper function to compute time range values
    const computeTimeRangeValues = useCallback((timeRange: TimeRangeSelection): { lastTime: string; offsetTime?: string } => {
        let lastTimeValue = Math.round(
            moment.duration(timeRange.endDate.diff(timeRange.startDate)).asSeconds()
        );
        let offsetTime: string | undefined;

        if (timeRange.range === CHART_CUSTOM_RANGE_OPTION) {
            lastTimeValue = Math.round(
                moment.duration(moment().diff(timeRange.endDate)).asSeconds()
            );

            offsetTime = convertSecondsToPrometheusTime(lastTimeValue);
            lastTimeValue = moment
                .duration(timeRange.endDate.diff(timeRange.startDate))
                .asSeconds();
        }

        const lastTime = convertSecondsToPrometheusTime(lastTimeValue);
        return { lastTime, offsetTime };
    }, []);

    // Fetch zone data
    const fetchZoneData = useCallback(async () => {
        if (!id || id === 'undefined' || !zoneId || !currentOrgId) {
            setIsPageLoading(false);
            return;
        }

        try {
            setIsPageLoading(true);
            // Tag API stores zone tags as "zone/<zoneName>"; pass full value so the API returns the tag
            const zoneTagValue = `zone/${zoneId}`;
            let zoneTags = await fetchTags(currentOrgId, zoneTagValue);
            // Normalize: API may return a single tag object instead of an array
            if (!Array.isArray(zoneTags)) {
                zoneTags = zoneTags && typeof zoneTags === "object" ? [zoneTags] : [];
            }
            // Fallback: if backend value filter returns no tag, fetch all tags and pick the zone tag client-side
            if (!zoneTags.length) {
                const allTags = await fetchTags(currentOrgId);
                const list = Array.isArray(allTags) ? allTags : [];
                zoneTags = list.filter((tag: any) => tag?.value === zoneTagValue);
            }
            const zoneTagUIDs = extractTagUIDs(zoneTags);
            const zoneTagUIDSAIDs = extractTagUIDSAIDs(zoneTags);
            zoneTagUIDSAIDsRef.current = zoneTagUIDSAIDs;
            
            const [registryRes, configRes, statusRes] = await Promise.all([
                getOneRegistry(id),
                getRegistryConfig(id),
                getGwAndLDSUStatus(id)
            ]);

            if (!isHttpSuccess(registryRes.status) || 
                !isHttpSuccess(configRes.status) || 
                !isHttpSuccess(statusRes.status)) {
                setIsPageLoading(false);
                return;
            }

            const devices = registryRes.data?.info?.devices || [];
            const configList = configRes.data || [];
            const statusDevices = statusRes.data?.devices || [];
            const registryUIDs = extractRegistryUIDs(registryRes.data);
            
            const zoneUIDs = findZoneUIDs(zoneTagUIDs, registryUIDs);
            const zoneRoomDeviceUIDs = findZoneRoomDeviceUIDs(zoneUIDs, devices);
            
            const allTags = await fetchTags(currentOrgId);
            const roomTags = allTags.filter((tag: any) => roomTagContainsZoneDevices(tag, zoneRoomDeviceUIDs));

            // Also create room entries for devices directly in the zone tag that are rooms
            // (not just room tags that contain zone room devices)
            // IMPORTANT: Only include devices with NAME="room", NOT NAME="zone" (to avoid showing zone as room)
            const directZoneRooms = buildDirectZoneRooms(zoneRoomDeviceUIDs, devices, roomTags);

            const { sensors: busSensors, actuators: allActuators } = collectBusDevices(devices, configList, statusDevices);
            // Include sensors from registry config for zone-tagged UID+SAIDs not returned by getLDSBusInfo (e.g. missing from LDSU status)
            const configSensors = getSensorsFromConfigForZoneTag(zoneTagUIDSAIDs, devices, configList);
            const busKeys = new Set(busSensors.map((s: any) => `${s.ldsu_uuid || s.UID}:${s.SAID}`));
            const configOnly = configSensors.filter((s: any) => !busKeys.has(`${s.ldsu_uuid}:${s.SAID}`));
            const allSensors = [...busSensors, ...configOnly];

            const selectedTimeRange = timeRange;
            const { lastTime, offsetTime } = computeTimeRangeValues(selectedTimeRange);

            // Fetch readings for sensors using the current time range
            const sensorsWithReadings = await mapSensorsWithReadings(
                allSensors,
                lastTime,
                offsetTime
            );

            const uidToDeviceMap = buildUIDToDeviceMap(devices);
            const zoneSensors = filterZoneSensors(
                sensorsWithReadings,
                zoneUIDs,
                zoneTagUIDSAIDs,
                uidToDeviceMap
            );
            const zoneActuators = filterZoneActuators(
                allActuators,
                zoneUIDs,
                zoneTagUIDSAIDs,
                uidToDeviceMap
            );

            const roomsData = buildRoomsData(
                roomTags,
                devices,
                sensorsWithReadings,
                allActuators,
                uidToDeviceMap
            );
            // Combine room tags with direct zone room devices
            const allRoomsData = [...roomsData, ...directZoneRooms];
            const roomTagUIDSAIDsByName = new Map<string, Map<string, Set<number>>>();
            for (const tag of roomTags) {
                const roomName = tag.value?.replace("room/", "") || "";
                if (!roomName) {
                    continue;
                }
                roomTagUIDSAIDsByName.set(roomName, extractTagUIDSAIDs([tag]));
            }
            roomTagUIDSAIDsByNameRef.current = roomTagUIDSAIDsByName;

            // Query room status for each room using strict per-UID semantics.
            const roomsWithStatus = await Promise.all(
                allRoomsData.map(async (room: any) => {
                    let roomStatus = "Unknown";
                    const roomTagUIDSAIDs = roomTagUIDSAIDsByName.get(room.name) || new Map<string, Set<number>>();
                    const roomUIDSAIDsForStatus = selectUIDSAIDsForUID(roomTagUIDSAIDs, room.uuid);
                    if (currentOrgId && id && roomUIDSAIDsForStatus.size > 0) {
                        try {
                            const result = await queryTagStatusFromUIDSAIDsWithTimestamp(
                                id,
                                (gatewayId: string) =>
                                    buildPromQLQueryFromUIDSAIDsMap(gatewayId, roomUIDSAIDsForStatus)
                            );
                            roomStatus = result.status;
                        } catch (error) {
                            console.error("ZoneDetails: Error querying room status for", room.name, ":", error);
                        }
                    }
                    return { ...room, status: roomStatus };
                })
            );

            // Count rooms using the same logic as NetworkDetails: count devices directly in zone tag that are rooms
            // This matches the list view count and includes devices with NAME="room" or OBJ=45058 in the zone tag
            const zoneRoomCounts = countDevicesByTagUIDSAIDs(zoneTagUIDSAIDs, devices);

            const zoneUuid = selectEntityUUID(zoneUIDs, devices, zoneId, "zone");
            const { status: zoneStatus, timestamp: zoneTimestamp } =
                await getZoneStatus(id, zoneTagUIDSAIDsRef.current, currentOrgId, zoneUuid);

            const zoneData = {
                name: zoneId,
                uuid: zoneUuid,
                sensors: zoneSensors.length,
                controllers: zoneActuators.length,
                rooms: zoneRoomCounts.rooms, // Use countDevicesByTagUIDSAIDs to match NetworkDetails
                status: zoneStatus,
                // Use backend-derived timestamp for zone status where available
                timestamp: zoneTimestamp ?? null,
            };

            setZone(zoneData);
            setZoneName(zoneId);
            setRooms(roomsWithStatus); // Combined rooms data with room status queried per row
            setSensors(zoneSensors);
            // Merge actuator status from getAllLastBusReading (same source as refresh)
            const mergeActuatorWithDevices = (a: any, devices: any[]) => {
                const device = devices.find((d: any) => d.UID === a.UID);
                if (!device) return a;
                const status = get(device.value, `[${a.said || 0}]`, null);
                return { ...a, readings: { status, timestamp: device.ts } };
            };
            const mergeActuatorsWithReadings = async (actuatorsList: any[]) => {
                const readingPromises = [];
                for (let busNumber = 1; busNumber <= 3; busNumber++) {
                    readingPromises.push(getAllLastBusReading(id, busNumber.toString()));
                }
                const results = await Promise.all(readingPromises);
                const allDevices: any[] = [];
                for (const r of results) {
                    if (isHttpSuccess(r.status) && r.data?.devices) allDevices.push(...r.data.devices);
                }
                return actuatorsList.map((a) => mergeActuatorWithDevices(a, allDevices));
            };
            const zoneActuatorsWithReadings = await mergeActuatorsWithReadings(zoneActuators);
            setControllers(zoneActuatorsWithReadings);

            setNetwork({
                name: registryRes.data?.name || "Network",
                id: id
            });

            await fetchAlertData();
            setIsPageLoading(false);
        } catch (error) {
            console.error("Error fetching zone data:", error);
            setIsPageLoading(false);
        }
    }, [
        id,
        zoneId,
        currentOrgId,
        fetchAlertData,
        fetchTags,
        extractTagUIDs,
        extractTagUIDSAIDs,
        extractRegistryUIDs,
        findZoneUIDs,
        findZoneRoomDeviceUIDs,
        roomTagContainsZoneDevices,
        collectBusDevices,
        mapSensorsWithReadings,
        buildUIDToDeviceMap,
        filterZoneSensors,
        filterZoneActuators,
        buildRoomsData,
        getZoneStatus,
        buildDirectZoneRooms,
        computeTimeRangeValues,
        timeRange,
    ]);

    // Dedicated function to update only the status of the zone
    const updateZoneStatus = useCallback(async () => {
        if (!id || !zoneId || !currentOrgId) {
            return;
        }

        setIsRefreshing(true);
        try {
            const { status, timestamp } = await getZoneStatus(
                id,
                zoneTagUIDSAIDsRef.current,
                currentOrgId,
                zone?.uuid
            );
            setZone((prevZone: any) => {
                if (!prevZone) return prevZone;
                const updatedZone = {
                    ...prevZone,
                    status,
                    // Update timestamp from backend where available, otherwise keep previous
                    timestamp: timestamp ?? prevZone.timestamp ?? null,
                };

                return updatedZone;
            });
        } catch (error) {
            console.error("Error updating zone status:", error);
        } finally {
            setIsRefreshing(false);
        }
    }, [id, zoneId, currentOrgId, getZoneStatus, zone]);

    useEffect(() => {
        setIsPageLoading(true);
        fetchZoneData();
    }, [fetchZoneData]);

    // Refresh all sensor readings
    const refreshAllSensorReadings = useCallback(async (timeRange?: string, offset?: string) => {
        if (!id || !sensors.length) return;
        
        setIsRefreshing(true);
        try {
            const sensorsWithReadings = await mapSensorsWithReadings(
                sensors,
                timeRange,
                offset
            );
            setSensors(sensorsWithReadings);
        } catch (error) {
            console.error("ZoneDetails: Error refreshing sensor readings:", error);
        } finally {
            setIsRefreshing(false);
        }
    }, [id, sensors, mapSensorsWithReadings, setIsRefreshing, setSensors]);

    // Single-row controller refresh: per-device reading2 (bulk refresh still uses all-bus read).
    const refreshOneController = useCallback(async (controllerUID: string, said: number) => {
        if (!id || !controllerUID) return;

        try {
            const { data, status } = await getSensorLastReading(id, controllerUID);
            if (!isHttpSuccess(status)) return;

            const parsed = getControllerStatusFromReading2(data, said);
            if (!parsed) return;

            const { status: updatedStatus, timestamp: updatedTimestamp } = parsed;

            setControllers((prevControllers: any[]) =>
                prevControllers.map((c: any) => {
                    if (c.UID === controllerUID && c.said === said) {
                        return {
                            ...c,
                            readings: {
                                value: updatedStatus,
                                status: updatedStatus,
                                timestamp: updatedTimestamp,
                            },
                        };
                    }
                    return c;
                })
            );
        } catch (error) {
            console.error("ZoneDetails: Error refreshing controller status:", error);
        }
    }, [id]);

    // Handle refresh controller click
    const handleRefreshController = useCallback(async (
        e: React.MouseEvent,
        controllerName: string
    ) => {
        e.stopPropagation();

        const target = e.currentTarget as HTMLButtonElement | null;

        // Find the controller by name
        const controller = controllers.find((c: any) => c.name === controllerName);
        if (!controller?.UID) {
            console.error("ZoneDetails: Controller not found for refresh");
            return;
        }

        if (target) {
            target.classList.add("refresh-state");
        }

        try {
            await refreshOneController(controller.UID, controller.said || 0);
        } finally {
            if (target) {
                target.classList.remove("refresh-state");
            }
        }
    }, [controllers, refreshOneController]);

    // Helper function to update a single controller with device data (readings from getAllLastBusReading)
    const updateControllerWithDevice = (controller: any, allDevices: any[]) => {
        const device = allDevices.find((d: any) => d.UID === controller.UID);
        if (!device) {
            return controller;
        }
        const updatedStatus = get(device.value, `[${controller.said || 0}]`, null);
        const updatedTimestamp = device.ts;
        return {
            ...controller,
            readings: {
                value: updatedStatus,
                status: updatedStatus,
                timestamp: updatedTimestamp,
            },
        };
    };

    // Refresh all controller statuses
    const refreshAllControllerStatuses = async () => {
        if (!id || !controllers.length) return;
        
        setIsRefreshing(true);
        try {
            // Check all buses (1-3) to get all controller readings
            const readingPromises = [];
            for (let busNumber = 1; busNumber <= 3; busNumber++) {
                readingPromises.push(getAllLastBusReading(id, busNumber.toString()));
            }
            const readingResults = await Promise.all(readingPromises);
            
            // Collect all device readings from all buses
            const allDevices: any[] = [];
            for (const result of readingResults) {
                if (isHttpSuccess(result.status) && result.data.devices) {
                    allDevices.push(...result.data.devices);
                }
            }
            
            // Update all controllers with their latest readings
            setControllers((prevControllers: any[]) => {
                return prevControllers.map((c: any) => updateControllerWithDevice(c, allDevices));
            });
        } catch (error) {
            console.error("ZoneDetails: Error refreshing all controller statuses:", error);
        } finally {
            setIsRefreshing(false);
        }
    };

    const refreshAllRoomStatuses = useCallback(async () => {
        if (!id || !currentOrgId || !rooms.length) return;

        setIsRoomStatusRefreshing(true);
        try {
            const updatedRooms = await Promise.all(
                rooms.map(async (room: any) => {
                    const roomTagUIDSAIDs =
                        roomTagUIDSAIDsByNameRef.current.get(room.name) ??
                        new Map<string, Set<number>>();
                    const roomUIDSAIDsForStatus = selectUIDSAIDsForUID(roomTagUIDSAIDs, room.uuid);
                    if (roomUIDSAIDsForStatus.size === 0) {
                        return { ...room, status: "Unknown" };
                    }

                    try {
                        const result = await queryRoomStatusWithTimeout(id, roomUIDSAIDsForStatus);
                        return { ...room, status: result.status };
                    } catch (error) {
                        console.error("ZoneDetails: Error refreshing room status for", room.name, ":", error);
                        return room;
                    }
                })
            );
            setRooms(updatedRooms);
        } finally {
            setIsRoomStatusRefreshing(false);
        }
    }, [id, currentOrgId, rooms]);

    // Refresh a single sensor's reading using getSensorLastReading
    const refreshOneSensor = useCallback(async (ldsuUUID: string, SAID: number) => {
        if (!id || !ldsuUUID) return;
        
        try {
            const { lastTime, offsetTime } = getPrometheusTimeRangeForSensorReadings(timeRange);
            const { data, status } = await getSensorLastReading(id, ldsuUUID, lastTime, offsetTime);
            if (isHttpSuccess(status)) {
                setSensors((prevSensors: any[]) => {
                    return prevSensors.map((s: any) => {
                        if (String(s.ldsu_uuid) === String(ldsuUUID) && String(s.SAID) === String(SAID)) {
                            const prevReadings = s.readings ?? {};

                            const rawValueCandidateRaw = get(data.value, `[${SAID}]`, "-");
                            const highestCandidateRaw = get(data.max, `[${SAID}]`, "-");
                            const lowestCandidateRaw = get(data.min, `[${SAID}]`, "-");

                            const valueCandidate = rawValueCandidateRaw ?? "-";
                            const highestCandidate = highestCandidateRaw ?? "-";
                            const lowestCandidate = lowestCandidateRaw ?? "-";

                            const updatedReading = mergeSensorLastReadingWithMinMaxFallback(
                                prevReadings,
                                {
                                    highest: highestCandidate,
                                    lowest: lowestCandidate,
                                    value: valueCandidate,
                                    timestamp: data?.ts,
                                }
                            );

                            return { ...s, readings: updatedReading };
                        }
                        return s;
                    });
                });
            }
        } catch (error) {
            console.error("ZoneDetails: Error refreshing sensor reading:", error);
        }
    }, [id, timeRange]);

    // Handle refresh sensor click
    const handleRefreshSensor = useCallback(async (
        e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
        sensor: any
    ) => {
        const target = e.currentTarget;
        if (!target) return;
        
        const sensorId = `${sensor.ldsu_uuid}-${sensor.SAID}`;
        target.dataset.sensorId = sensorId;
        target.classList.add("refresh-state");
        
        try {
            await refreshOneSensor(sensor.ldsu_uuid, sensor.SAID);
        } finally {
            const element = document.querySelector(`.refresh-sensor[data-sensor-id="${sensorId}"]`) as HTMLButtonElement;
            if (element) {
                element.classList.remove("refresh-state");
                delete element.dataset.sensorId;
            }
        }
    }, [refreshOneSensor]);

    const toggleZoneNameEdit = () => {
        setIsZoneNameEditing(!isZoneNameEditing);
    };

    const toggleZoneNameUpdate = () => {
        setIsZoneNameEditing(false);
        if (zone) {
            setZone({ ...zone, name: zoneName });
        }
    };

    const handleRowClick = useCallback((row: any, tabType?: string) => {
        if (!validateAndRedirect()) {
            return;
        }

        const encodedZoneId = encodeURIComponent(zoneId ?? "");
        const basePath = `/network-details/${id}/zone/${encodedZoneId}`;
        switch (tabType) {
            case 'rooms':
                history.push(
                    `${basePath}/room-details/${encodeURIComponent(row.name)}${
                        row.uuid ? `?uuid=${encodeURIComponent(row.uuid)}` : ""
                    }`
                );
                break;
            case 'sensors':
                // Pass full sensor data through navigation state (similar to NetworkDetails.tsx)
                history.push({
                    pathname: `${basePath}/sensor-details/${row.ldsu_uuid}-${row.SAID}`,
                    state: {
                        sensorDetails: row,
                        gatewayName: "Network Gateway",
                        portNumber: "1",
                        UUID: id,
                        sensorTimeRange: {
                            range: timeRange.range,
                            startDate: timeRange.startDate.valueOf(),
                            endDate: timeRange.endDate.valueOf(),
                        },
                    }
                });
                break;
            case 'controllers':
                if (row.UID != null && row.said != null) {
                    history.push(`${basePath}/controller-details/${row.UID}-${row.said}`);
                }
                break;
            default:
                break;
        }
    }, [validateAndRedirect, id, zoneId, history, timeRange]);

    const rowToContext: NetworkRowToContext = useMemo(
        () => ({ page: "zone", networkId: id ?? "", zoneId: zoneId ?? "" }),
        [id, zoneId]
    );
    const getRowTo = useCallback(
        (row: any, tabType?: string) => getRowToForContext(rowToContext, row, tabType),
        [rowToContext]
    );
    
    const renderOnOffHighlighted = useCallback((text: string) => {
        if (!text) return '-';
        const parts = String(text).split(/\b(on|off)\b/i);
        return (
            <span>
                {parts.map((part, idx) => {
                    const lower = part.toLowerCase();
                    const isTarget = lower === 'on' || lower === 'off';
                    const uniqueKey = `${idx}-${part}`;
                    return isTarget ? (
                        <span key={uniqueKey} style={{ color: '#00cbaf' }}>{part}</span>
                    ) : (
                        <span key={uniqueKey}>{part}</span>
                    );
                })}
            </span>
        );
    }, []);

    // Helper to get default column keys for each tab type when no data is available
    const getDefaultColumnKeys = (tabKey: string): string[] => {
        switch (tabKey) {
            case 'rooms':
                return ['name', 'sensors', 'controllers', 'status'];
            case 'sensors':
                return ['name', 'type', 'reading', 'minmax', 'lastactive', 'alert'];
            case 'controllers':
                return ['name', 'type', 'status'];
            default:
                return [];
        }
    };

    // Helper to get empty message for each tab type
    const getEmptyMessage = (tabKey: string): string => {
        if (tabKey === 'rooms') {
            return 'No Rooms to display';
        }
        if (tabKey === 'sensors') {
            return 'No Sensors to display';
        }
        if (tabKey === 'controllers') {
            return 'No Actuators to display';
        }
        return 'No data to display';
    };

    // Helper to get data source for column generation (prioritize tabData, fallback to state)
    const getDataForColumns = (tabKey: string) => {
        // First try tabData (filtered data)
        if (tabData[tabKey] && tabData[tabKey].length > 0) {
            return tabData[tabKey];
        }
        // Fallback to state variables (unfiltered data)
        let stateData: any[] = [];
        if (tabKey === 'rooms') stateData = rooms;
        else if (tabKey === 'sensors') stateData = processedSensors;
        else if (tabKey === 'controllers') stateData = processedControllers;
        
        if (stateData && Array.isArray(stateData) && stateData.length > 0) {
            return stateData;
        }
        // If no data, create a sample object with default keys for this tab type
        const defaultKeys = getDefaultColumnKeys(tabKey);
        if (defaultKeys.length > 0) {
            const sampleObject: any = {};
            for (const key of defaultKeys) {
                sampleObject[key] = null;
            }
            return [sampleObject];
        }
        return null;
    };

    const shouldIncludeColumn = (col: string, tabKey: string) => {
        const lowerCol = col.toLowerCase();

        if (
            tabKey === "controllers" &&
            (lowerCol === "lastactive" || lowerCol.includes("lastactive") ||
             lowerCol === "uid" || lowerCol === "said")
        ) {
            return false;
        }

        if (
            tabKey === "sensors" &&
            (lowerCol === "ldsu_uuid" || lowerCol === "said")
        ) {
            return false;
        }

        // Hide uuid field from room table; it is used only for navigation/query, not display.
        if (tabKey === "rooms" && lowerCol === "uuid") {
            return false;
        }

        if (tabKey === "rooms" && lowerCol === "statussortvalue") {
            return false;
        }

        if (tabKey === "sensors" && lowerCol === "minmax" && col !== "minMax") {
            return false;
        }

        if (tabKey === "sensors") {
            const allowedColumns = [
                "name",
                "sensor_name",
                "type",
                "reading",
                "minmax",
                "lastactive",
                "alert",
            ];
            return allowedColumns.includes(lowerCol);
        }

        return true;
    };

    const getColumnLabel = (col: string, tabKey: string) => {
        const colLower = col.toLowerCase();
        if (tabKey === "rooms" && colLower === "status") {
            return (
                <span>
                    <span>Status</span>
                    <IconButton
                        disableRipple
                        className={
                            isRoomStatusRefreshing
                                ? "mr-2 bigRefresh refresh-animation"
                                : "mr-2 bigRefresh"
                        }
                        style={{ cursor: "pointer", marginLeft: 8, background: "none", border: "none", padding: 0 }}
                        aria-label="Refresh all room statuses"
                        onClick={(e) => {
                            e.stopPropagation();
                            refreshAllRoomStatuses();
                        }}
                        onKeyDown={(e) => {
                            e.stopPropagation();
                            refreshAllRoomStatuses();
                        }}
                    >
                        <img src={refreshIcon} alt="refresh" />
                    </IconButton>
                </span>
            );
        }

        return getNetworkColumnLabel({
            col,
            tabKey,
            tabLabels,
            isRefreshing,
            refreshIcon,
            onRefreshSensors: refreshAllSensorReadings,
            onRefreshControllers: refreshAllControllerStatuses,
            extraColumnLabels: {
                "rooms:sensors": "No. of Sensors",
                "rooms:controllers": "No. of Actuators",
            },
        });
    };

    const createAlertToggleHandler = useCallback((ldsuUuid: string, said: number) => {
        return () => {
            const isCurrentlyEnabled = returnCheckboxStatus(ldsuUuid, said);
            const saidString = String(said);
            if (isCurrentlyEnabled) {
                updateAlertStatus(ldsuUuid, saidString);
            } else {
                walletModalTrigger(() => updateAlertStatus(ldsuUuid, saidString));
            }
        };
    }, [returnCheckboxStatus, updateAlertStatus]);

    // Memoized render function factories
    const createNameLinkRenderer = useCallback((col: string, tabKey: string) => {
        return (row: any) => (
            <NameLinkRenderer
                row={row}
                col={col}
                tabKey={tabKey}
                onRowClick={handleRowClick}
                getRowTo={getRowTo}
            />
        );
    }, [handleRowClick, getRowTo]);

    const createReadingRenderer = useCallback((col: string) => {
        return (row: any) => (
            <ReadingRenderer row={row} col={col} />
        );
    }, []);

    const createMinMaxRenderer = useCallback((col: string) => {
        return (row: any) => (
            <MinMaxRenderer row={row} col={col} />
        );
    }, []);

    const createLastActiveRenderer = useCallback(
        (col: string, tabKey: string) => {
            return (row: any) =>
                renderLastActiveCellCommon({
                    row,
                    col,
                    tabKey,
                    onRefreshSensor: handleRefreshSensor,
                    refreshAltText: "refresh sensor 2",
                });
        },
        [handleRefreshSensor]
    );

    const createStatusRenderer = useCallback(
        (col: string, tabKey: string) => {
            return (row: any) => {
                const statusValue = row[col];
                return renderStatusCellCommon({
                    statusValue,
                    tabKey,
                    controllerName: row.name,
                    onRefreshController: (e, controllerName) => {
                        void handleRefreshController(e, controllerName);
                    },
                    renderOnOffHighlighted,
                    onRefreshClickWrapper: (e, handler) => {
                        e.stopPropagation();
                        handler(e);
                    },
                });
            };
        },
        [renderOnOffHighlighted, handleRefreshController]
    );

    const createAlertRenderer = useCallback(() => {
        return (row: any) => (
            <AlertRenderer
                row={row}
                createAlertToggleHandler={createAlertToggleHandler}
                returnCheckboxStatus={returnCheckboxStatus}
                returnDisabledStatus={returnDisabledStatus}
            />
        );
    }, [createAlertToggleHandler, returnCheckboxStatus, returnDisabledStatus]);

    const getColumnRenderer = (col: string, tabKey: string) => {
        const lowerCol = col.toLowerCase();

        if (
            tabKey === "sensors" &&
            (lowerCol === "name" || lowerCol === "sensor_name")
        ) {
            const renderer = createSensorNameRenderer(tabKey, handleRowClick, getRowTo);
            return (row: any) => renderer(row);
        }

        if (lowerCol === "name" && (tabKey === "rooms" || tabKey === "controllers")) {
            return createNameLinkRenderer(col, tabKey);
        }

        if (lowerCol === "reading" && tabKey === "sensors") {
            return createReadingRenderer(col);
        }

        if (
            tabKey === "sensors" &&
            (lowerCol === "minmax" || lowerCol.includes("minmax"))
        ) {
            return createMinMaxRenderer(col);
        }

        if (lowerCol === "lastactive" || lowerCol.includes("lastactive")) {
            return createLastActiveRenderer(col, tabKey);
        }

        if (lowerCol === "status") {
            return createStatusRenderer(col, tabKey);
        }

        if (lowerCol === "alert") {
            return createAlertRenderer();
        }

        return (row: any) => row[col];
    };

    // Reusable function to generate columns from data
    const generateColumnsFromData = (data: any[], tabKey: string) => {
        if (!data || data.length === 0) return [];

        return Object.keys(data[0])
            .filter((col: string) => shouldIncludeColumn(col, tabKey))
            .map((col) => {
                const lowerCol = col.toLowerCase();
                const isSensorTab = tabKey === "sensors";
                let sortKey: string | undefined;

                if (isSensorTab) {
                    if (lowerCol === "reading") {
                        sortKey = "readingSortValue";
                    } else if (lowerCol === "minmax" || lowerCol.includes("minmax")) {
                        sortKey = "minMaxSortValue";
                    } else if (lowerCol === "lastactive" || lowerCol.includes("lastactive")) {
                        sortKey = "readings.timestamp";
                    } else if (lowerCol === "alert") {
                        sortKey = "alertSortValue";
                    }
                } else if (tabKey === "controllers") {
                    if (lowerCol === "name" || lowerCol === "type" || lowerCol === "status") {
                        sortKey = col;
                    }
                } else if (tabKey === "rooms" && lowerCol === "status") {
                    sortKey = "statusSortValue";
                }

                return {
                    label: getColumnLabel(col, tabKey),
                    key: col,
                    render: getColumnRenderer(col, tabKey),
                    sortKey,
                };
            });
    };

    // Memoized custom renderers for sensors tab
    const sensorsCustomRenderers = useMemo(() => ({
        sensor_name: (value: any, row: any) =>
            createSensorNameRenderer("sensors", handleRowClick, getRowTo)(row),
        reading: createReadingRenderFunction(),
        minMax: createMinMaxRenderFunction(),
        lastactive: createLastActiveRenderFunction(handleRefreshSensor, isRefreshing),
        alert: createAlertRenderFunction(
            createAlertToggleHandler,
            returnCheckboxStatus,
            returnDisabledStatus
        )
    }), [handleRowClick, getRowTo, handleRefreshSensor, isRefreshing, createAlertToggleHandler, returnCheckboxStatus, returnDisabledStatus]);

    // Show "Zone not found" or loading state when zone is null
    if (!zone) {
        return (
            <ContentWrapper isLoading={isPageLoading}>
                <div className="page-content">
                    <Container fluid>
                        <Row>
                            <Col sm={6} className="ZoneDetails-header">
                                <Row>
                                    <Col>
                                        <h5 className="page-title">Zone Details</h5>
                                    </Col>
                                    <Col sm="12">
                                        <Breadcrumb className="w-100">
                                            <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/networks" }}>
                                                All Networks
                                            </Breadcrumb.Item>
                                            <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/network-details/${id}` }}>
                                                Network Details
                                            </Breadcrumb.Item>
                                            <Breadcrumb.Item active>Zone Details</Breadcrumb.Item>
                                        </Breadcrumb>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>

                        <Row>
                            <Col sm={8}>
                                <div className="zone-not-found-panel" style={{
                                    backgroundColor: '#2f3a51',
                                    borderRadius: '8px',
                                    padding: '24px',
                                    marginBottom: '20px'
                                }}>
                                    <div style={{
                                        color: '#ff3860',
                                        fontSize: '18px',
                                        fontWeight: 'bold',
                                        marginBottom: '16px'
                                    }}>
                                        {isPageLoading ? 'Loading zone data...' : 'Zone not found.'}
                                    </div>
                                    {!isPageLoading && (
                                    <div style={{ color: '#a0a0a0' }}>
                                        The requested zone could not be found in this network.
                                    </div>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </ContentWrapper>
        );
    }

    return (
        <ContentWrapper isLoading={isPageLoading}>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col sm={6} className="ZoneDetails-header">
                            <Row>
                                <Col>
                                    <h5 className="page-title">{zone.name}</h5>
                                </Col>
                                <Col sm="12">
                                    <Breadcrumb className="w-100">
                                        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/networks" }}>
                                            All Networks
                                        </Breadcrumb.Item>
                                        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/network-details/${id}` }}>
                                            Network Details
                                        </Breadcrumb.Item>
                                        <Breadcrumb.Item active>Zone</Breadcrumb.Item>
                                    </Breadcrumb>
                                </Col>
                            </Row>
                        </Col>
                    </Row>

                    <Row>
                        <Col sm={6}>
                            <div className="zone-summary rounded shadow-sm">
                                <div className="form-box mb-2" style={{ paddingLeft: '30px', paddingRight: '30px', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
                                    <div>
                                        <div className="descriptor-item d-flex" style={{ borderBottom: "none" }}>
                                            <h5 className="flex-grow-1">Status of Zone</h5>
                                            <div style={{ textAlign: "right" }}>
                                                {(() => {
                                                    const isOn = zone.status === "On";
                                                    const isOff = zone.status === "Off";
                                                    const dotColor = isOn ? "#00d1b2" : "#ff3860";
                                                    let label = "Unknown";
                                                    if (isOff) {
                                                        label = "Off";
                                                    } else if (isOn) {
                                                        label = "On";
                                                    }

                                                    return (
                                                        <>
                                                            <span
                                                                style={{
                                                                    color: dotColor,
                                                                    marginRight: 8,
                                                                }}
                                                            >
                                                                &#9679;
                                                            </span>
                                                            {label}{" "}
                                                            <span style={{ color: '#828FAB' }}>
                                                                {renderDateTime(zone.timestamp)}
                                                            </span>
                                                        </>
                                                    );
                                                })()}
                                                <IconButton
                                                    disableRipple
                                                    className={
                                                        isRefreshing
                                                            ? "mr-2 ml-2 bigRefresh refresh-animation"
                                                            : "mr-2 ml-2 bigRefresh"
                                                    }
                                                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                                    aria-label="Refresh zone status"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        updateZoneStatus();
                                                    }}
                                                    onKeyDown={() => updateZoneStatus()}
                                                >
                                                    <img
                                                        src={refreshIcon}
                                                        alt="refresh"
                                                    />
                                                </IconButton>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="zone-info-group">
                                    <div>
                                        <Form>
                                            <div className="form-box mb-2">
                                                <Form.Row>
                                                    <Col md="12">
                                                        <Form.Label>
                                                            Zone Name
                                                        </Form.Label>
                                                    </Col>
                                                    <Col
                                                        lg={10}
                                                        xs={12}
                                                        className="mb-2"
                                                    >
                                                        <Form.Group className="mb-0">
                                                            <Form.Control
                                                                type="text"
                                                                required
                                                                aria-label="zone-name"
                                                                defaultValue={zoneName}
                                                                disabled={!isZoneNameEditing}
                                                                onChange={(e) => {
                                                                    setZoneName(e.target.value);
                                                                }}
                                                                onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                                    if (e.key === "Enter") {
                                                                        e.preventDefault();
                                                                        toggleZoneNameUpdate();
                                                                    }
                                                                }}
                                                            />
                                                        </Form.Group>
                                                    </Col>

                                                    <Col lg={2} xs="12">
                                                        {isZoneNameEditing ? (
                                                            <Button
                                                                type="button"
                                                                className="w-100"
                                                                onClick={toggleZoneNameUpdate}
                                                            >
                                                                Update
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                type="button"
                                                                className="w-100"
                                                                onClick={toggleZoneNameEdit}
                                                                disabled={true}
                                                            >
                                                                EDIT
                                                            </Button>
                                                        )}
                                                    </Col>
                                                </Form.Row>
                                            </div>
                                        </Form>
                                    </div>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} className="d-flex flex-column">
                            <div className="form-box zone-summary rounded shadow-sm" style={{ padding: '0.5rem 30px' }}>
                                <div className="zone-info-group">
                                    <div>
                                        <div className="descriptor-item d-flex" style={{ borderBottom: "1px solid #3e4b67" }}>
                                            <h6 className="flex-grow-1">Zone UUID</h6>
                                            <h6 className="ginfo float-right">{zone?.uuid || '-'}</h6>
                                        </div>
                                        <div className="descriptor-item d-flex" style={{ borderBottom: "1px solid #3e4b67" }}>
                                            <h6 className="flex-grow-1">No. of Sensors</h6>
                                            <h6 className="ginfo float-right">{zone.sensors ?? '-'}</h6>
                                        </div>
                                        <div className="descriptor-item d-flex" style={{ borderBottom: "1px solid #3e4b67" }}>
                                            <h6 className="flex-grow-1">No. of Actuators</h6>
                                            <h6 className="ginfo float-right">{zone.controllers ?? '-'}</h6>
                                        </div>
                                        <div className="descriptor-item d-flex" style={{ borderBottom: "1px solid #3e4b67" }}>
                                            <h6 className="flex-grow-1">No. of Rooms</h6>
                                            <h6 className="ginfo float-right">{zone.rooms ?? '-'}</h6>
                                        </div>
                                        <div className="descriptor-item d-flex" style={{ borderBottom: "1px solid #3e4b67" }}>
                                            <h6 className="flex-grow-1">Network Name</h6>
                                            <h6 className="ginfo float-right">{network?.name || '-'}</h6>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col sm={12}>
                            <div className="page-tabs zonedetails-tabs mt-3">
                                <Tab.Container
                                    id="zone-details-tabs"
                                    activeKey={activeTab}
                                    onSelect={(k) => setActiveTab(k || "rooms")}
                                >
                                    <Row>
                                        <Col sm={12}>
                                            <Nav className="flex-nowrap" style={{ whiteSpace: 'nowrap' }}>
                                                {tabKeys.map((key) => (
                                                    <Nav.Item key={key} style={{ width: '300px' }}>
                                                        <Nav.Link eventKey={key}>
                                                            <h6>
                                                                {tabLabels[key]}{" "}
                                                                <span className="count">
                                                                    {tabData[key] ? tabData[key].length : 0}
                                                                </span>
                                                            </h6>
                                                        </Nav.Link>
                                                    </Nav.Item>
                                                ))}
                                            </Nav>
                                        </Col>
                                        <Col sm={12}>
                                            <Tab.Content>
                                                {tabKeys.map((key) => (
                                                    <Tab.Pane key={key} eventKey={key}>
                                                        <div style={{ borderRadius: 8, padding: 0 }}>
                                                            {key === 'sensors' && !IS_BASIC_PLAN && (
                                                                <WalletAlertComponent showWarningWhen="LowBalance" />
                                                            )}
                                                            <TabTableWithSearch
                                                                data={tabData[key] || []}
                                                                emptyMessage={getEmptyMessage(key)}
                                                                columns={
                                                                    (() => {
                                                                        // Try to get data from tabData first, then fallback to state
                                                                        const dataForColumns = getDataForColumns(key);
                                                                        if (dataForColumns) {
                                                                            return generateColumnsFromData(dataForColumns, key);
                                                                        }
                                                                        // If no data available at all, return empty array (no headers will show)
                                                                        return [];
                                                                    })()
                                                                }
                                                                placeholder={`Search...`}
                                                                onRowClick={handleRowClick}
                                                                getRowTo={getRowTo}
                                                                tabType={key}
                                                                showArrowColumn={true}
                                                                customRenderers={
                                                                    getTabType('sensors', tabData[key] || []) ? sensorsCustomRenderers : {}
                                                                }
                                                                customFilters={renderSensorsControllersFilters({
                                                                    tabKey: key,
                                                                    sensorTypes,
                                                                    actuatorTypes,
                                                                    selectedSensorTypes,
                                                                    setSelectedSensorTypes,
                                                                    selectedActuatorTypes,
                                                                    setSelectedActuatorTypes,
                                                                })}
                                                            />
                                                        </div>
                                                    </Tab.Pane>
                                                ))}
                                            </Tab.Content>
                                        </Col>
                                    </Row>
                                </Tab.Container>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
            <LoadingModal showModal={isAlertLoading} />
        </ContentWrapper>
    );
};

export default ZoneDetails;